'use client';

import { useFocusable } from '@react-aria/interactions';
import { cloneElement, useRef } from 'react';
import type { Key, ReactElement, RefObject } from 'react';
import type { TooltipProps, TooltipTriggerComponentProps } from 'react-aria-components';
import { Tooltip as AriaTooltip, TooltipTrigger } from 'react-aria-components';

import { useFusionTheme } from '../hooks';

import styles from './Tooltip.module.scss';

type ReactElementWithRef = ReactElement & { props: { ref: RefObject<null> } };

export interface Props
  extends Pick<
      TooltipProps,
      | 'isOpen'
      | 'onOpenChange'
      | 'className'
      | 'placement'
      | 'offset'
      | 'crossOffset'
      | 'shouldFlip'
    >,
    Pick<
      TooltipTriggerComponentProps,
      'isDisabled' | 'delay' | 'closeDelay' | 'trigger' | 'defaultOpen'
    > {
  children: [ReactElementWithRef, ReactElement | string];
  tooltipKey?: Key;
}

const Trigger = (props: { children: ReactElementWithRef }) => {
  const ref = useRef(null);
  const { focusableProps } = useFocusable({}, ref);

  const triggerElement = cloneElement(props.children, {
    ref,
    ...focusableProps
  });

  return triggerElement;
};

const Tooltip = ({
  children: [triggerContent, tooltipContent],
  trigger,
  isOpen,
  className,
  placement = 'bottom',
  isDisabled,
  offset,
  crossOffset,
  shouldFlip = true,
  delay,
  closeDelay,
  defaultOpen,
  tooltipKey,
  onOpenChange
}: Props) => {
  const { addThemeClassName } = useFusionTheme();

  return (
    <TooltipTrigger
      trigger={trigger}
      isDisabled={isDisabled}
      delay={delay}
      closeDelay={closeDelay}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      defaultOpen={defaultOpen}
    >
      <Trigger>{triggerContent}</Trigger>
      <AriaTooltip
        key={tooltipKey}
        shouldFlip={shouldFlip}
        offset={offset}
        crossOffset={crossOffset}
        className={addThemeClassName(styles.tooltip, className)}
        placement={placement}
      >
        {tooltipContent}
      </AriaTooltip>
    </TooltipTrigger>
  );
};
export default Tooltip;
